/*
DO NOT REMOVE THIS COMMENT!

The document highlighting system uses non-whitespace character-based offsets with these key characteristics:
1. Offset Calculation:
- Offsets count non-whitespace characters from the start of each block
- Start.Offset is always 0 when highlight starts at beginning of a block/paragraph
2. Block Behavior:
- Each block starts counting from 0
- Blocks count independently
- Adjacent highlights in same block share offset values (end of one = start of next)
3. Position Structure in Annotation:
- position: {
    start: {
        blockId: number;
        offset: number;
    };
    end: {
        blockId: number;
        offset: number;
    };
}
*/

import { cloneElement, createElement, Fragment, isValidElement } from 'react';
import { Annotation } from '../../Annotation/types';
import Highlight from '../../Highlighting/components/Highlight';
import { v4 as uuidv4 } from 'uuid';
import { getNonWhitespaceCount } from './highlightPositionUtils';

interface TextSegment {
  text: string;
  annotation?: Annotation;
}

interface BlockInfo {
  id: number;
  parentId: number | null;
}

function parseValidBlockId(id?: string): number | null {
  const BLOCK_ID_PREFIX = 'b';
  const BASE_10 = 10;

  if (!id?.startsWith(BLOCK_ID_PREFIX)) {
    return null;
  }

  const parsedId = parseInt(id.slice(1), BASE_10);
  return isNaN(parsedId) ? null : parsedId;
}

/**
 * Finds the closest block ID for a given node
 * Returns the block ID and the element that contains it
 */
function findClosestBlockId(node: Node): {
  blockId: number | null;
  element: Node | null;
} {
  let currentElement: Node | null = node;

  while (currentElement) {
    // Process only element nodes
    if (currentElement.nodeType === Node.ELEMENT_NODE) {
      const element = currentElement as Element;
      const parsedId = parseValidBlockId(element.id);

      if (parsedId !== null) {
        return { blockId: parsedId, element: currentElement };
      }
    }

    currentElement = currentElement.parentNode;
  }

  return { blockId: null, element: null };
}

/**
 * Finds the closest block ID(s) for the current node
 * Returns both the immediate block ID and any parent block ID
 */
function findBlockInfo(node: Node): BlockInfo | null {
  // Find the immediate block ID
  const { blockId, element: blockElement } = findClosestBlockId(node);

  // If no block ID was found, return null
  if (blockId === null || blockElement === null) {
    return null;
  }

  // Find the parent block ID by starting from the parent of the block element
  const parentNode = blockElement.parentNode;
  let parentId: number | null = null;

  if (parentNode) {
    const result = findClosestBlockId(parentNode);
    parentId = result.blockId;
  }

  // Return block info with both IDs
  return { id: blockId, parentId };
}

function isHighlightedBlock(annotation: Annotation, blockId: number): boolean {
  const { start, end } = annotation.position;

  // If there is no parent block, the highlight cannot be in the parent block
  if (blockId === null) {
    return false;
  }

  // If the highlight starts or ends in the block, highlight is applied to the block
  if (start.blockId === blockId || end.blockId === blockId) {
    return true;
  }

  // If the highlight starts before the block and ends after the block, highlight is applied to the block
  return start.blockId < blockId && end.blockId > blockId;
}

function getAnnotationsAppliedToBlock(
  annotations: Annotation[],
  blockInfo: BlockInfo,
) {
  const { id, parentId } = blockInfo;

  return annotations
    .filter((annotation) => {
      // Check if highlight applies to current block
      if (isHighlightedBlock(annotation, id)) {
        return true;
      }

      // If there's a parent block, check if highlight applies there
      if (parentId !== null) {
        return isHighlightedBlock(annotation, parentId);
      }
      return false;
    })
    .sort((a, b) => {
      if (a.position.start.blockId === b.position.start.blockId) {
        return a.position.start.offset - b.position.start.offset;
      }
      return a.position.start.blockId - b.position.start.blockId;
    });
}

/**
 * Adjusts highlight positions for block boundaries
 */
function adjustHighlightForBlock(
  annotation: Annotation,
  blockInfo: BlockInfo,
  totalNonWhitespaceChars: number,
): Annotation {
  const { id: blockId, parentId } = blockInfo;
  const adjusted = { ...annotation };

  // If highlight started in a previous block or parent block,
  // start at beginning of this text
  if (
    annotation.position.start.blockId < blockId ||
    (parentId !== null && annotation.position.start.blockId <= parentId)
  ) {
    adjusted.position = {
      ...adjusted.position,
      start: { blockId, offset: 0 },
    };
  }

  // If highlight continues to next block,
  // end at end of this text
  if (annotation.position.end.blockId > blockId) {
    adjusted.position = {
      ...adjusted.position,
      end: { blockId, offset: totalNonWhitespaceChars },
    };
  }

  return adjusted;
}

/**
 * Creates text segments with highlights for a block of text
 */
function createTextSegments(
  text: string,
  annotations: Annotation[],
  blockInfo: BlockInfo,
): TextSegment[] {
  if (!text) {
    return [];
  }

  // If there are no highlights applied to the block, return the text as a single segment
  if (annotations.length === 0) {
    return [{ text }];
  }

  const totalNonWhitespaceChars = getNonWhitespaceCount(text);
  const adjustedAnnotations = annotations.map((a) =>
    adjustHighlightForBlock(a, blockInfo, totalNonWhitespaceChars),
  );

  // Create segments
  const segments: TextSegment[] = [];
  let currentIndex = 0;

  for (const annotation of adjustedAnnotations) {
    const startPos = findIndexFromNonWhitespaceOffset(
      text,
      annotation.position.start.offset,
    );
    const endPos = findIndexFromNonWhitespaceOffset(
      text,
      annotation.position.end.offset,
    );

    if (startPos > currentIndex) {
      segments.push({ text: text.slice(currentIndex, startPos) });
    }

    const highlightedText = text.slice(startPos, endPos);
    if (highlightedText) {
      segments.push({ text: highlightedText, annotation });
    }

    currentIndex = endPos;
  }

  if (currentIndex < text.length) {
    segments.push({ text: text.slice(currentIndex) });
  }

  return segments;
}

function findIndexFromNonWhitespaceOffset(
  text: string,
  offset: number,
): number {
  if (offset <= 0) {
    return 0;
  }

  let nonWhitespaceCount = 0;
  let index = 0;

  while (index < text.length && nonWhitespaceCount < offset) {
    if (!/\s/.test(text[index])) {
      nonWhitespaceCount++;
    }
    index++;
  }

  return index;
}

/**
 * Maps HTML attribute names to React prop names
 */
const attributeMap: Record<string, string> = {
  class: 'className',
  for: 'htmlFor',
  tabindex: 'tabIndex',
  readonly: 'readOnly',
  maxlength: 'maxLength',
  contenteditable: 'contentEditable',
  colspan: 'colSpan',
  rowspan: 'rowSpan',
};

/**
 * Parses an inline style string into a React style object
 */
function parseInlineStyle(styleStr: string): Record<string, string> {
  const style: Record<string, string> = {};
  if (!styleStr) {
    return style;
  }

  styleStr.split(';').forEach((declaration) => {
    const [property, value] = declaration.split(':').map((s) => s.trim());
    if (property && value) {
      // Convert kebab-case to camelCase for React
      const camelProperty = property.replace(/-([a-z])/g, (_, letter) =>
        letter.toUpperCase(),
      );
      style[camelProperty] = value;
    }
  });

  return style;
}

/**
 * Gets all attributes from a DOM element and converts them to React props
 */
function getElementAttributes(
  element: Element,
): Record<string, string | Record<string, string>> {
  const attributes: Record<string, string | Record<string, string>> = {};
  Array.from(element.attributes).forEach((attr) => {
    // Convert HTML attribute names to React prop names
    const reactPropName = attributeMap[attr.name] || attr.name;

    // Handle style attribute specially by converting to React style object
    if (reactPropName === 'style') {
      attributes.style = parseInlineStyle(attr.value);
    } else {
      attributes[reactPropName] = attr.value;
    }
  });
  return attributes;
}

/**
 * Transforms a text node by applying highlights
 */
function transformTextNode(
  node: Node,
  annotations: Annotation[],
): React.ReactNode {
  const content = node.textContent ?? '';
  if (!content.trim()) {
    return content;
  }

  const blockInfo = findBlockInfo(node);
  if (!blockInfo) {
    return content;
  }

  const annotationsAppliedToBlock = getAnnotationsAppliedToBlock(
    annotations,
    blockInfo,
  );

  const segments = createTextSegments(
    content,
    annotationsAppliedToBlock,
    blockInfo,
  );

  return segments.map((segment, index) =>
    segment.annotation ? (
      <Highlight
        key={`${segment.annotation.id}-${index}`}
        annotation={segment.annotation}
      >
        {segment.text}
      </Highlight>
    ) : (
      segment.text
    ),
  );
}

/**
 * Transforms an element node by processing its children
 */
function transformElementNode(
  element: Element,
  annotations: Annotation[],
): React.ReactNode {
  const tagName = element.tagName.toLowerCase();

  // For void elements, don't process children
  if (VOID_ELEMENTS.has(tagName)) {
    return createElement(tagName, {
      ...getElementAttributes(element),
      key: element.id ?? element.getAttribute('data-tp') ?? undefined,
    });
  }

  const isTableElement = TABLE_ELEMENTS.has(tagName);

  // Process children, preserving whitespace between elements (except in tables)
  const children = Array.from(element.childNodes)
    .filter((child) => {
      if (child.nodeType !== Node.TEXT_NODE) {
        return true; // Keep all non-text nodes
      }

      // For table elements, remove all whitespace-only text nodes
      if (isTableElement && child.textContent?.trim() === '') {
        return false;
      }

      // For non-table elements, keep all text nodes, including whitespace-only ones.
      // The previous logic for removing whitespace-only text nodes
      // unless they were between two elements is now removed to preserve all whitespace.
      return true;
    })
    .map((child, index) => {
      const result = transformNode(child, annotations);

      if (child.nodeType === Node.TEXT_NODE && typeof result === 'string') {
        return (
          <Fragment key={`${element.id || ''}-${index}`}>{result}</Fragment>
        );
      }

      if (isValidElement(result) && !result.key) {
        return cloneElement(result, { key: `${element.id || ''}-${index}` });
      }

      return result;
    });

  return createElement(
    tagName,
    {
      ...getElementAttributes(element),
      key: element.id ?? element.getAttribute('data-tp') ?? undefined,
    },
    children,
  );
}

/**
 * Transforms a <d> element into a Fragment while preserving (and processing) children.
 */
function transformDataNode(element: Element, annotations: Annotation[]) {
  const children = Array.from(element.childNodes).map((child, index) => {
    const result = transformNode(child, annotations);
    if (child.nodeType === Node.TEXT_NODE && typeof result === 'string') {
      return (
        <Fragment key={`${element.id || ''}-text-${index}`}>{result}</Fragment>
      );
    }
    if (isValidElement(result) && !result.key) {
      return cloneElement(result, {
        key: `${element.id || ''}-element-${index}`,
      });
    }
    return result;
  });

  // Add a key to the outer Fragment using the element's ID or a generated key
  return (
    <Fragment
      key={
        element.id ?? element.getAttribute('data-tp') ?? `fragment-${uuidv4()}`
      }
    >
      {children}
    </Fragment>
  );
}

/**
 * Main entry point for transforming nodes with highlights
 */
function transformNode(node: Node, annotations: Annotation[]): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return transformTextNode(node, annotations);
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;

    // Transform <d> elements into Fragments while preserving children
    if (element.nodeName.toLowerCase() === 'd') {
      return transformDataNode(element, annotations);
    }

    return transformElementNode(element, annotations);
  }

  return null;
}

/**
 * Main entry point for transforming HTML with highlights
 */
export function applyHighlights(node: Node, annotations: Annotation[]) {
  return transformNode(node, annotations);
}

const VOID_ELEMENTS = new Set([
  'img',
  'br',
  'hr',
  'area',
  'base',
  'basefont',
  'col',
  'embed',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

const TABLE_ELEMENTS = new Set([
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'colgroup',
]);
